import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  buildCourseVersionDetailsRoute,
  buildCourseSessionDetailsRoute
} from "../../../../routes/AdminRoutes";
import { useDispatch, useSelector } from "react-redux";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { push } from "connected-react-router";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import {
  fetchCourseVersionData,
  fetchCourseSections,
  addItemIntoShortCourse,
  addRepositoryItemsIntoShortCourse
} from "../../../../redux/actions/contentCoursesActions";
import {
  addUpload,
  markJobFinished,
  fetchItem,
  updateItem
} from "../../../../redux/actions/adminConsole/content/courseBuilderActions";
import Modal from "../../../../components/Modal/Modal";
import ProcessingModal from "../../../../components/ProcessingModal/ProcessingModal";
import {
  activityTypes,
  courseBuilderButtonIds
} from "../../../../constants/index";
import CreateShortCoursePage from "./CreateShortCoursePage";
import AddItemFromRepository from "./AddItemFromRepository";
import AddLinkModal from "../../../../components/CourseBuilder/AddLinkModal";
import UploadFileModal from "../../../../components/CourseBuilder/UploadFileModal";
import EditCourseActivity from "../../../../components/CourseBuilder/EditCourseActivity";
import CourseQuizContainer from "./CourseQuiz";
import CourseSurveyContainer from "./CourseSurvey";
import { ActionCableConsumer } from "react-actioncable-provider";
import CourseBuilderContent from "./CourseBuilderContent";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import EditHomework from "./EditHomework";
import AddCourse from "./AddCourse";
import { useCourses } from "../../../../hooks/courseBuilder";
import { serialize } from "../../../../redux/utils/jsona";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import { showJobWarnings } from "../../../../utils/repositoryUtils";

const selector = makeGetEntities();

const getCurrentCourseSectionId = (currentEditItem, currentShortCourse) => {
  if (currentEditItem) {
    return currentEditItem.shortCourseId;
  } else if (currentShortCourse) {
    return currentShortCourse.id;
  } else {
    return null;
  }
};

const courseBuilderTabs = [
  {
    value: "mandatory",
    title: "courseVersion.details.tableContents"
  },
  {
    value: "optional",
    title: "courseVersion.details.resources"
  }
];

const DublicateCourseNoticeModal = ({ open, onClose, dublicatedItems }) => {
  const { t } = useTranslation();

  return (
    <Modal
      scrollable
      disableEnforceFocus
      showCloseIcon
      maxWidth="sm"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t(`dublicateCourseNoticeModal.title`)}
        </Typography>
      }
      dialogContent={
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            mb={5.5}
            flexDirection={{ xs: "column", sm: "row" }}
            flexWrap="wrap"
          >
            <Typography variant="body2" color="primary">
              {t(`dublicateCourseNoticeModal.description`)}
            </Typography>
            <ul>
              {dublicatedItems.map(name => {
                return (
                  <li key={name.name}>
                    <Typography variant="body2" color="primary">
                      {name.name}({name.course_name})
                    </Typography>
                  </li>
                );
              })}
            </ul>
          </Box>
        </>
      }
      dialogActions={
        <>
          <Button color="primary" onClick={onClose}>
            {t("dublicateCourseNoticeModal.cancelBtn")}
          </Button>
        </>
      }
      open={open}
      onClose={onClose}
    />
  );
};

const CourseBuilderTitle = ({ courseName }) => {
  return (
    <Breadcrumb
      routes={["Courses", courseName, "Manage Version", "Course Builder"]}
    />
  );
};

const CourseBuilder = ({
  match: {
    params: { courseId, versionId }
  }
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [openModalType, setOpenModalType] = useState("");
  const [
    openDublicateCourseNoticeModal,
    setOpenDublicateCourseNoticeModal
  ] = useState(false);
  const [itemBeingEdited, setItemBeingEdited] = useState(null);
  const [currentShortCourse, setCurrentShortCourse] = useState(null);
  const [runningJobId, setRunningJobId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [dublicatedItems, setDublicatedItems] = useState([]);
  const { addCourses } = useCourses();

  const { versionData, isUploading, customer } = useSelector(
    ({ contentCoursesState, acCourseBuilderState, customerState }) => ({
      versionData: selector(contentCoursesState, {
        type: "version",
        id: versionId
      }),
      isFetching: contentCoursesState.loading,
      isUploading: acCourseBuilderState.loading,
      customer: customerState.customer
    })
  );

  const isPlatinumCustomer = () => {
    return customer && customer.packageName === "Platinum";
  };

  const getContent = () => {
    dispatch(fetchCourseSections(versionId));
  };

  useEffect(() => {
    getContent();
    dispatch(fetchCourseVersionData(courseId, versionId));
  }, []);

  useEffect(() => {
    if (!openModalType) {
      setItemBeingEdited(null);
    }
  }, [openModalType]);

  function handleTabChange(newTab) {
    setCurrentTab(newTab);
  }
  const handleGoBackToCourseVersionsDetails = type => {
    if (type === "ILT") {
      dispatch(push(buildCourseSessionDetailsRoute(courseId, versionId)));
    } else {
      dispatch(push(buildCourseVersionDetailsRoute(courseId, versionId)));
    }
  };

  const handleItemDropOnShortCourse = (shortCourse, item) => {
    if (shortCourse) {
      setCurrentShortCourse(shortCourse);
    }
    if (item) {
      setOpenModalType(item.id);
    }
  };

  // TODO: Implement functionality for adding new page to short course
  const handleCreateNewPage = (shortCourseId, newPageData) => {
    dispatch(
      addItemIntoShortCourse(versionId, shortCourseId, "html", newPageData)
    );
  };

  const handleAddItems = selectedRowIds => {
    if (currentShortCourse) {
      const data = {
        repositoryItemsIds: selectedRowIds
      };
      dispatch(
        addRepositoryItemsIntoShortCourse(
          versionId,
          currentShortCourse.id,
          data
        )
      );
      setOpenModalType("");
    }
  };

  const handleUploadFile = newFileData => {
    dispatch(
      addUpload(versionId, currentShortCourse.id, newFileData, res => {
        if (res.jobId) {
          setRunningJobId(res.jobId);
        } else {
          getContent();
        }
      })
    );
    setOpenModalType("");
  };

  const handleAddLink = newLinkData => {
    let data = {
      link: newLinkData.link,
      title: newLinkData.title || newLinkData.link,
      description: newLinkData.description,
      openInNewTab: newLinkData.openInNewTab
    };
    dispatch(
      addItemIntoShortCourse(
        versionId,
        currentShortCourse.id,
        "link",
        serialize({ type: "item", ...data })
      )
    );
    setOpenModalType("");
  };

  const handleAddCourses = courseIds => {
    addCourses(versionId, courseIds)
      .then(response => {
        if (response.items_not_added) {
          setDublicatedItems(response.items_not_added);
          setOpenDublicateCourseNoticeModal(true);
        }
        getContent();
        setOpenModalType("");
      })
      .catch(_e => {
        // TODO: add error handling for failure to add courses
      });
  };

  const handleCloseModal = () => {
    setOpenModalType("");
  };
  const handleCloseDublicateCourseNoticeModal = () => {
    setOpenDublicateCourseNoticeModal(false);
  };

  const handleEditActivity = (
    itemId,
    shortCourseId,
    activityType,
    shortCourse
  ) => {
    setCurrentShortCourse(shortCourse);
    if (activityType === activityTypes.homework) {
      setEditItem({ shortCourseId: shortCourseId, itemId: itemId });
      setOpenModalType(activityTypes.homework);
    } else {
      dispatch(
        fetchItem(versionId, shortCourseId, itemId, () => {
          setEditItem({ shortCourseId: shortCourseId, itemId: itemId });
        })
      );
    }
  };

  const handleUpdateActivity = (itemData, errorCallback) => {
    dispatch(
      updateItem(
        versionId,
        editItem.shortCourseId,
        editItem.itemId,
        itemData,
        res => {
          if (res.jobId) {
            setRunningJobId(res.jobId);
          } else {
            setEditItem(null);
            getContent();
          }
        },
        errorCallback
      )
    );
    setOpenModalType("");
  };

  return (
    <AdminPageLayout
      title={
        <CourseBuilderTitle
          courseName={versionData ? versionData.name : ""}
          releaseVersion={versionData ? versionData.release : ""}
        />
      }
      onGoBackClick={() =>
        handleGoBackToCourseVersionsDetails(versionData ? versionData.type : "")
      }
    >
      <Tabs
        value={currentTab}
        onChange={(e, newTab) => handleTabChange(newTab)}
        indicatorColor="primary"
        textColor="primary"
      >
        {courseBuilderTabs.map((tab, index) => (
          <Tab key={index} label={t(tab.title)} />
        ))}
      </Tabs>
      <ProcessingModal
        open={isUploading}
        messageText={t("courseBuilder.fileUploadProcessingMessage")}
      />
      {!!runningJobId && (
        <ActionCableConsumer
          channel={{
            channel: "ScormProgressChannel",
            job_id: runningJobId
          }}
          onReceived={response => {
            dispatch(markJobFinished());
            getContent();
            setEditItem(null);
            setRunningJobId(null);
            showJobWarnings(response);
            if (!response.result) {
              dispatch(
                enqueueErrorSnackbar({
                  message: t("courseBuilder." + response.title)
                })
              );
            }
          }}
        />
      )}
      <CourseBuilderContent
        sectionType={courseBuilderTabs[currentTab].value}
        onDropItem={handleItemDropOnShortCourse}
        versionId={versionId}
        onEditActivity={handleEditActivity}
        isPlatinumCustomer={isPlatinumCustomer()}
      />
      <CreateShortCoursePage
        open={openModalType === courseBuilderButtonIds.page}
        onClose={() => handleCloseModal()}
        shortCourseTitle={currentShortCourse ? currentShortCourse.title : ""}
        shortCourseId={currentShortCourse ? currentShortCourse.id : null}
        onSubmitNewPage={handleCreateNewPage}
      />
      <AddItemFromRepository
        open={openModalType === courseBuilderButtonIds.repositoryItem}
        onClose={() => handleCloseModal()}
        shortCourseId={currentShortCourse ? currentShortCourse.id : null}
        onAddItems={handleAddItems}
        courseOrPolicy={"course"}
      />
      <DublicateCourseNoticeModal
        open={openDublicateCourseNoticeModal}
        onClose={handleCloseDublicateCourseNoticeModal}
        dublicatedItems={dublicatedItems}
      />
      <AddCourse
        open={openModalType === courseBuilderButtonIds.course}
        onClose={() => handleCloseModal()}
        trainingObjectId={versionId}
        onAddCourses={handleAddCourses}
        getRowId={row => row.minorCtoaId}
      />
      <AddLinkModal
        open={openModalType === courseBuilderButtonIds.link}
        onClose={handleCloseModal}
        onAddLink={handleAddLink}
        itemEditing={itemBeingEdited}
      />
      <UploadFileModal
        open={openModalType === courseBuilderButtonIds.upload}
        onClose={handleCloseModal}
        onUploadFile={handleUploadFile}
      />
      <EditCourseActivity
        open={!!editItem}
        onClose={() => setEditItem(null)}
        onSave={handleUpdateActivity}
        shortCourseTitle={currentShortCourse ? currentShortCourse.title : ""}
        versionId={versionId}
        shortCourseId={editItem ? editItem.shortCourseId : null}
      />
      <CourseQuizContainer
        open={openModalType === courseBuilderButtonIds.quiz}
        onClose={() => handleCloseModal()}
        versionId={versionId}
        shortCourseId={(currentShortCourse || {}).id}
      />
      <CourseSurveyContainer
        open={openModalType === courseBuilderButtonIds.survey}
        onClose={() => handleCloseModal()}
        versionId={versionId}
        shortCourseId={(currentShortCourse || {}).id}
      />
      <EditHomework
        open={openModalType === courseBuilderButtonIds.homework}
        onClose={() => {
          setEditItem(null);
          setOpenModalType("");
        }}
        trainingObjectId={versionId}
        courseSectionId={getCurrentCourseSectionId(
          editItem,
          currentShortCourse
        )}
        existingHomeworkId={editItem ? editItem.itemId : null}
      />
    </AdminPageLayout>
  );
};

export default CourseBuilder;
